import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import GlassmorphismTokens from '../../styles/GlassmorphismTokens';
import { extractTimeFromActivity, cleanMissingDataNote } from '../../pages/ItineraryPage.utils';
import NudgeChips from './NudgeChips';

const TimelineContainer = styled.div`
  padding: 8px 16px 16px 16px;
  position: relative;

  ${props => props.$isMobile && css`
    padding: 8px 0 16px 0;
  `}
`;

/* ─── Time-of-day atmosphere colors ─── */
const TIME_ATMOSPHERES = {
  morning: 'rgba(255, 183, 77, 0.06)',
  afternoon: 'rgba(59, 130, 246, 0.04)',
  evening: 'rgba(139, 92, 246, 0.06)',
};

/* ─── Continuous vertical timeline line ─── */
const TimeBlockSection = styled.div`
  position: relative;
  margin-bottom: 8px;
  border-radius: 16px;
  background: ${props => TIME_ATMOSPHERES[props.$timeBlock] || 'transparent'};
  padding: ${props => props.$timeBlock ? '8px 0' : '0'};
  transition: background 0.3s ease;

  /* Vertical line running through dots */
  &::before {
    content: '';
    position: absolute;
    left: 15px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: rgba(0, 0, 0, 0.08);
  }

  &:last-child {
    margin-bottom: 0;
  }

  ${props => props.$isMobile && css`
    &::before {
      display: none;
    }
  `}
`;

const TIME_BLOCK_ICONS = { morning: '\u2600\uFE0F', afternoon: '\u26C5', evening: '\uD83C\uDF19' };

const TimeBlockLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${props => props.$color || '#6b7280'};
  margin-bottom: 12px;
  padding-left: 44px;
  display: flex;
  align-items: center;
  gap: 6px;

  ${props => props.$isMobile && css`
    display: none;
  `}
`;

/* ─── Mobile time row with line ─── */
const MobileTimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  padding: 0 4px;
`;

const MobileTimeLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
  flex-shrink: 0;
`;

const MobileTimeLine = styled.div`
  flex: 1;
  height: 1px;
  background: rgba(0, 0, 0, 0.08);
`;

/* ─── Single activity row ─── */
const TimelineItem = styled(motion.div)`
  position: relative;
  display: flex;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 16px;
  cursor: pointer;

  &:last-child {
    margin-bottom: 8px;
  }

  ${props => props.$isMobile && css`
    gap: 0;
    margin-bottom: 16px;
  `}
`;

/* ─── Colored dot with ring ─── */
const TimelineDot = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${props => props.$color || '#f59e0b'};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
  ring: 4px;
  box-shadow: ${props => props.$isSelected
    ? `0 0 0 4px rgba(66, 133, 244, 0.4), 0 0 0 2px white`
    : `0 0 0 4px rgba(255, 255, 255, 0.5)`};
  transition: all 0.2s ease;
  margin-top: 4px;

  font-size: 12px;
  font-weight: 700;
  color: white;

  ${props => props.$isMobile && css`
    display: none;
  `}
`;


/* ─── White card ─── */
const ActivityCard = styled.div`
  flex: 1;
  position: relative;
  background: ${props => props.$isSelected
    ? 'rgba(232, 240, 254, 1)'
    : 'white'};
  border-radius: 16px;
  padding: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
  display: flex;
  gap: 14px;
  border: 1.5px solid ${props => props.$isSelected
    ? '#4285F4'
    : 'transparent'};

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);

  }

  ${props => props.$isMobile && css`
    border-radius: 24px;
    padding: 12px;
    gap: 12px;
  `}
`;

/* ─── Image thumbnail (LEFT side) ─── */
const ActivityThumbnail = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
    background: rgba(0, 0, 0, 0.04);
  }

  ${props => props.$isMobile && css`
    border-radius: 16px;
  `}
`;

const ActivityInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ActivityTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 4px 0;
  line-height: 1.3;
`;

const ActivityDescription = styled.p`
  font-size: 12px;
  color: #6b7280;
  margin: 0 0 8px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

/* ─── AI Why box (desktop) ─── */
const AIWhyBox = styled.div`
  background: #f9fafb;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #f3f4f6;
  margin-bottom: 8px;
`;

const AIWhyText = styled.p`
  font-size: 11px;
  color: #374151;
  margin: 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const AIWhyLabel = styled.span`
  font-weight: 700;
  font-style: italic;
  color: #1f2937;
`;

/* ─── Mobile AI reason (italic gray inline) ─── */
const MobileAIReason = styled.p`
  font-size: 12px;
  font-style: italic;
  color: #9ca3af;
  margin: 0 0 4px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
`;

const MetaBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  color: #6b7280;
  background: #f3f4f6;
  padding: 2px 8px;
  border-radius: 9999px;
`;

/* ─── Vibe Badge ─── */
const VibeBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 9999px;
  background: ${props => props.$type === 'popular' ? 'rgba(66, 133, 244, 0.1)' : props.$type === 'hidden' ? 'rgba(22, 163, 74, 0.1)' : 'rgba(20, 184, 166, 0.1)'};
  color: ${props => props.$type === 'popular' ? '#1A73E8' : props.$type === 'hidden' ? '#16A34A' : '#0D9488'};
`;


/* ─── Trust Badges ─── */

const TrustBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 10px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 9999px;
  line-height: 1.4;

  ${props => props.$confidence === 'high' && css`
    background: rgba(66, 133, 244, 0.12);
    color: #1A73E8;
  `}

  ${props => props.$confidence === 'medium' && css`
    background: transparent;
    color: #4285F4;
    border: 1px solid rgba(66, 133, 244, 0.3);
  `}

  ${props => props.$confidence === 'ai' && css`
    background: linear-gradient(135deg, rgba(66, 133, 244, 0.08), rgba(139, 92, 246, 0.08));
    color: #6b7280;
    border: 1px solid rgba(139, 92, 246, 0.15);
  `}

  ${props => props.$confidence === 'amber' && css`
    background: rgba(245, 158, 11, 0.1);
    color: #B45309;
    border: 1px solid rgba(245, 158, 11, 0.2);
  `}
`;

const TrustRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 6px;
`;

/* ─── "Why This?" Expandable Reasoning ─── */

const WhyThisToggle = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  color: #4285F4;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 2px 0;
  margin-top: 4px;
  transition: color 0.15s;

  &:hover {
    color: #1A73E8;
  }

  svg {
    width: 12px;
    height: 12px;
    transition: transform 0.2s;
  }
`;

const WhyThisContent = styled(motion.div)`
  margin-top: 8px;
  padding: 10px 12px;
  background: rgba(66, 133, 244, 0.05);
  border-left: 3px solid #4285F4;
  border-radius: 0 8px 8px 0;
`;

const WhyThisText = styled.p`
  margin: 0;
  font-size: 11px;
  font-style: italic;
  color: #374151;
  line-height: 1.5;
`;

const WhyThisSource = styled.span`
  display: block;
  margin-top: 6px;
  font-size: 10px;
  color: #9ca3af;
  font-style: normal;
`;

/* ─── Time label positioned to the left of the dot ─── */
const TimeLabel = styled.span`
  position: absolute;
  left: -4px;
  top: 38px;
  font-size: 10px;
  font-weight: 600;
  color: #6b7280;
  white-space: nowrap;
  width: 32px;
  text-align: center;
`;


const TIME_COLORS = {
  morning: GlassmorphismTokens.timeColors?.morning?.dot || '#f59e0b',
  afternoon: GlassmorphismTokens.timeColors?.afternoon?.dot || '#3b82f6',
  evening: GlassmorphismTokens.timeColors?.evening?.dot || '#8b5cf6',
};

const TIME_LABEL_COLORS = {
  morning: GlassmorphismTokens.timeColors?.morning?.text || '#92400e',
  afternoon: GlassmorphismTokens.timeColors?.afternoon?.text || '#1e40af',
  evening: GlassmorphismTokens.timeColors?.evening?.text || '#5b21b6',
};

const getPlaceholderEmoji = (timeBlock) => {
  switch (timeBlock) {
    case 'morning': return '\u{1F305}';
    case 'afternoon': return '\u2600\uFE0F';
    case 'evening': return '\u{1F319}';
    default: return '\u{1F4CD}';
  }
};

/* ─── Trust & Transparency Helpers ─── */

const getTrustBadges = (activity) => {
  if (typeof activity === 'string') return [];

  const badges = [];

  // Popularity
  const popularity = activity.popularityScore || activity.popularity_score;
  if (popularity && popularity > 100) {
    const formatted = popularity >= 1000
      ? `${(popularity / 1000).toFixed(1)}k`
      : popularity;
    badges.push({ label: `Popular with ${formatted} travelers`, confidence: 'high' });
  }

  // Rating
  const rating = activity.rating || activity.averageRating;
  if (rating && rating >= 3.5) {
    badges.push({ label: `${rating}/5 stars`, confidence: 'high' });
  }

  // Season
  const season = activity.bestSeason || activity.seasonBadge;
  if (season) {
    badges.push({ label: season, confidence: 'medium' });
  }

  // Verification
  const verified = activity.lastVerifiedAt || activity.last_verified_at;
  if (verified) {
    const verifiedDate = new Date(verified);
    const monthsAgo = Math.floor((Date.now() - verifiedDate) / (1000 * 60 * 60 * 24 * 30));
    if (monthsAgo <= 6) {
      const label = verifiedDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      badges.push({ label: `Verified ${label}`, confidence: 'high' });
    }
  } else if (activity.aiSuggested || activity.ai_suggested) {
    badges.push({ label: 'Suggested for you', confidence: 'ai' });
  }

  return badges;
};

const getDataFreshness = (activity) => {
  if (typeof activity === 'string') return null;
  const verified = activity.lastVerifiedAt || activity.last_verified_at;
  if (!verified) return null;

  const verifiedDate = new Date(verified);
  const monthsAgo = Math.floor((Date.now() - verifiedDate) / (1000 * 60 * 60 * 24 * 30));
  return { date: verifiedDate, stale: monthsAgo > 6 };
};

const buildWhyReasons = (activity) => {
  if (typeof activity === 'string') return null;

  const reasons = [];

  // AI reasoning
  const whyFun = activity.whyFunForProfile || activity.reasoning;
  if (whyFun) {
    reasons.push(cleanMissingDataNote(whyFun));
  }

  // Rating
  const rating = activity.rating || activity.averageRating;
  if (rating && rating >= 4.0) {
    reasons.push(`Top-rated in this area (${rating} stars)`);
  }

  // Preference match
  const prefMatch = activity.preferenceMatch || activity.matchReason;
  if (prefMatch) {
    reasons.push(prefMatch);
  }

  if (reasons.length === 0) return null;
  return reasons.join(' + ');
};

/* ─── Expandable "Why This?" Wrapper ─── */

const WhyThisSection = ({ activity }) => {
  const [expanded, setExpanded] = useState(false);
  const reasoning = buildWhyReasons(activity);
  if (!reasoning) return null;

  const sources = [];
  if (activity.rating || activity.averageRating) sources.push('traveler reviews');
  if (activity.localGuide) sources.push('local guides');
  if (activity.unescoSite) sources.push('UNESCO database');
  if (sources.length === 0) sources.push('travel insights');

  return (
    <>
      <WhyThisToggle onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}>
        Why this?
        <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
          <path d="M3 5l3 3 3-3" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </WhyThisToggle>
      <AnimatePresence>
        {expanded && (
          <WhyThisContent
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
          >
            <WhyThisText>
              Chosen because: {reasoning}
            </WhyThisText>
            <WhyThisSource>Data from: {sources.join(', ')}</WhyThisSource>
          </WhyThisContent>
        )}
      </AnimatePresence>
    </>
  );
};

/* ─── Vibe Badge Helper ─── */
const getVibeBadge = (activity) => {
  if (typeof activity === 'string') {
    const lower = activity.toLowerCase();
    if (/famous|popular|landmark|iconic/.test(lower)) return { label: 'Popular Spot', type: 'popular' };
    if (/local|hidden|offbeat|neighborhood/.test(lower)) return { label: 'Hidden Gem', type: 'hidden' };
    if (/authentic|traditional/.test(lower)) return { label: 'Local Favorite', type: 'local' };
    return null;
  }
  const text = `${activity.activity || ''} ${activity.description || ''} ${activity.name || ''}`.toLowerCase();
  if (/famous|popular|landmark|iconic/.test(text)) return { label: 'Popular Spot', type: 'popular' };
  if (/local|hidden|offbeat|neighborhood/.test(text)) return { label: 'Hidden Gem', type: 'hidden' };
  if (/authentic|traditional/.test(text)) return { label: 'Local Favorite', type: 'local' };
  return null;
};

const renderActivityItem = (activity, idx, timeBlock, dotColor, { onActivityClick, dayIndex, globalIndex, selectedActivityId, isMobile, photoCache }) => {
  const activityId = `day${dayIndex}-${timeBlock}-${idx}`;
  const isSelected = selectedActivityId === activityId;

  if (typeof activity === 'string') {
    const { time, title } = extractTimeFromActivity(activity);
    return (
      <React.Fragment key={idx}>
        {isMobile && time && (
          <MobileTimeRow>
            <MobileTimeLabel>{time}</MobileTimeLabel>
            <MobileTimeLine />
          </MobileTimeRow>
        )}
        <TimelineItem
          $isSelected={isSelected}
          $isMobile={isMobile}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: idx * 0.05 }}
          onClick={() => onActivityClick && onActivityClick(activity, dayIndex, idx, activityId)}
        >
          <TimelineDot $color={dotColor} $isSelected={isSelected} $isMobile={isMobile}>
            {globalIndex}
          </TimelineDot>
          {!isMobile && time && <TimeLabel>{time}</TimeLabel>}
          <ActivityCard $isSelected={isSelected} $isMobile={isMobile}>
            <ActivityThumbnail $isMobile={isMobile}>
              {photoCache?.[activityId] ? (
                <img src={photoCache[activityId]} alt={title} loading="lazy" width={80} height={80} />
              ) : (
                <div className="placeholder">{getPlaceholderEmoji(timeBlock)}</div>
              )}
            </ActivityThumbnail>
            <ActivityInfo>
              <ActivityTitle>{title}</ActivityTitle>
            </ActivityInfo>
          </ActivityCard>
        </TimelineItem>
      </React.Fragment>
    );
  }

  const activityText = activity.activity || '';
  const { time, title } = extractTimeFromActivity(activityText);
  const cleanedDesc = activity.description ? cleanMissingDataNote(activity.description) : '';
  const cleanedWhyFun = activity.whyFunForProfile ? cleanMissingDataNote(activity.whyFunForProfile) : '';
  const imageUrl = photoCache?.[activityId] || activity.image || activity.imageUrl || null;

  return (
    <React.Fragment key={idx}>
      {isMobile && time && (
        <MobileTimeRow>
          <MobileTimeLabel>{time}</MobileTimeLabel>
          <MobileTimeLine />
        </MobileTimeRow>
      )}
      <TimelineItem
        $isSelected={isSelected}
        $isMobile={isMobile}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: idx * 0.05 }}
        onClick={() => onActivityClick && onActivityClick(activity, dayIndex, idx, activityId)}
      >
        <TimelineDot $color={dotColor} $isSelected={isSelected} $isMobile={isMobile}>
          {globalIndex}
        </TimelineDot>
        {!isMobile && time && <TimeLabel>{time}</TimeLabel>}
        <ActivityCard $isSelected={isSelected} $isMobile={isMobile}>
          <ActivityThumbnail $isMobile={isMobile}>
            {imageUrl ? (
              <img src={imageUrl} alt={title} loading="lazy" width={80} height={80} />
            ) : (
              <div className="placeholder">{getPlaceholderEmoji(timeBlock)}</div>
            )}
          </ActivityThumbnail>
          <ActivityInfo>
            <ActivityTitle>{title}</ActivityTitle>

            {isMobile ? (
              /* Mobile: show description, fall back to AI reason if no description */
              (() => {
                const text = cleanedDesc?.trim() || cleanedWhyFun?.trim();
                return text ? <MobileAIReason>{text}</MobileAIReason> : null;
              })()
            ) : (
              /* Desktop: description + AI Why box */
              <>
                {cleanedDesc && cleanedDesc.trim().length > 0 && (
                  <ActivityDescription>{cleanedDesc}</ActivityDescription>
                )}
                {cleanedWhyFun && cleanedWhyFun.trim().length > 0 && (
                  <AIWhyBox>
                    <AIWhyText>
                      <AIWhyLabel>Why here: </AIWhyLabel>
                      {cleanedWhyFun}
                    </AIWhyText>
                  </AIWhyBox>
                )}
              </>
            )}

            <MetaRow>
              {activity.duration && activity.duration !== 'null' && (
                <MetaBadge>{activity.duration}</MetaBadge>
              )}
              {(() => {
                const vb = getVibeBadge(activity);
                return vb ? <VibeBadge $type={vb.type}>{vb.label}</VibeBadge> : null;
              })()}
            </MetaRow>

            {/* Trust Badges */}
            {(() => {
              const badges = getTrustBadges(activity);
              const freshness = getDataFreshness(activity);
              if (badges.length === 0 && !freshness) return null;
              return (
                <TrustRow>
                  {badges.map((badge, bi) => (
                    <TrustBadge key={bi} $confidence={badge.confidence}>
                      {badge.confidence === 'high' && (
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><path d="M5 0l1.12 3.44H9.8L6.84 5.56l1.12 3.44L5 6.88 2.04 9 3.16 5.56.2 3.44h3.68z"/></svg>
                      )}
                      {badge.label}
                    </TrustBadge>
                  ))}
                  {freshness && freshness.stale && (
                    <TrustBadge $confidence="amber">Data may be outdated</TrustBadge>
                  )}
                </TrustRow>
              );
            })()}

            {/* "Why This?" Expandable Section */}
            {!isMobile && <WhyThisSection activity={activity} />}
          </ActivityInfo>
        </ActivityCard>
      </TimelineItem>
    </React.Fragment>
  );
};

const ActivityTimeline = ({ day, dayIndex, onActivityClick, selectedActivityId, isMobile, photoCache = {}, onMoreLikeThis, onSomethingDifferent, isOwnItinerary }) => {
  const hasMorning = day.morning && day.morning.length > 0;
  const hasAfternoon = day.afternoon && day.afternoon.length > 0;
  const hasEvening = day.evening && day.evening.length > 0;
  const hasActivities = day.activities && Array.isArray(day.activities) && day.activities.length > 0;

  const groupedActivities = hasActivities ? {
    morning: day.activities.filter(a => a.time?.toLowerCase() === 'morning'),
    afternoon: day.activities.filter(a => a.time?.toLowerCase() === 'afternoon'),
    evening: day.activities.filter(a => a.time?.toLowerCase() === 'evening'),
  } : null;

  const morning = hasMorning ? day.morning : groupedActivities?.morning || [];
  const afternoon = hasAfternoon ? day.afternoon : groupedActivities?.afternoon || [];
  const evening = hasEvening ? day.evening : groupedActivities?.evening || [];

  let globalCounter = 1;
  const morningStart = globalCounter;
  globalCounter += morning.length;
  const afternoonStart = globalCounter;
  globalCounter += afternoon.length;
  const eveningStart = globalCounter;

  const sharedProps = { onActivityClick, dayIndex, selectedActivityId, isMobile, photoCache };

  return (
    <TimelineContainer $isMobile={isMobile}>
      {morning.length > 0 && (
        <TimeBlockSection $isMobile={isMobile} $timeBlock="morning">
          <TimeBlockLabel $color={TIME_LABEL_COLORS.morning} $isMobile={isMobile}>{TIME_BLOCK_ICONS.morning} Morning</TimeBlockLabel>
          {morning.map((activity, idx) =>
            renderActivityItem(activity, idx, 'morning', TIME_COLORS.morning, {
              ...sharedProps,
              globalIndex: morningStart + idx,
            })
          )}
          {isOwnItinerary && (
            <NudgeChips
              dayIndex={dayIndex}
              timeBlock="morning"
              activities={morning}
              onMoreLikeThis={onMoreLikeThis}
              onSomethingDifferent={onSomethingDifferent}
              isMobile={isMobile}
            />
          )}
        </TimeBlockSection>
      )}

      {afternoon.length > 0 && (
        <TimeBlockSection $isMobile={isMobile} $timeBlock="afternoon">
          <TimeBlockLabel $color={TIME_LABEL_COLORS.afternoon} $isMobile={isMobile}>{TIME_BLOCK_ICONS.afternoon} Afternoon</TimeBlockLabel>
          {afternoon.map((activity, idx) =>
            renderActivityItem(activity, idx, 'afternoon', TIME_COLORS.afternoon, {
              ...sharedProps,
              globalIndex: afternoonStart + idx,
            })
          )}
          {isOwnItinerary && (
            <NudgeChips
              dayIndex={dayIndex}
              timeBlock="afternoon"
              activities={afternoon}
              onMoreLikeThis={onMoreLikeThis}
              onSomethingDifferent={onSomethingDifferent}
              isMobile={isMobile}
            />
          )}
        </TimeBlockSection>
      )}

      {evening.length > 0 && (
        <TimeBlockSection $isMobile={isMobile} $timeBlock="evening">
          <TimeBlockLabel $color={TIME_LABEL_COLORS.evening} $isMobile={isMobile}>{TIME_BLOCK_ICONS.evening} Evening</TimeBlockLabel>
          {evening.map((activity, idx) =>
            renderActivityItem(activity, idx, 'evening', TIME_COLORS.evening, {
              ...sharedProps,
              globalIndex: eveningStart + idx,
            })
          )}
          {isOwnItinerary && (
            <NudgeChips
              dayIndex={dayIndex}
              timeBlock="evening"
              activities={evening}
              onMoreLikeThis={onMoreLikeThis}
              onSomethingDifferent={onSomethingDifferent}
              isMobile={isMobile}
            />
          )}
        </TimeBlockSection>
      )}

      {morning.length === 0 && afternoon.length === 0 && evening.length === 0 && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
          No activities for this day.
        </div>
      )}
    </TimelineContainer>
  );
};

export default ActivityTimeline;
